/**
 * Field Guide architecture — single source of truth for the map-usage
 * documentation product.  Drives routes, sitemap, prerender, hreflang,
 * content lookup.
 *
 * Field Guide is a PRODUCT-NEUTRAL companion to the country Atlas.  Atlas
 * is "what places exist"; Field Guide is "how to use the tool to find them."
 *
 * URLs:
 *   /guide                          — index (EN)
 *   /ru/guide                       — index (RU)
 *   /guide/<section>                — section index (EN)
 *   /guide/<section>/<article>      — article (EN)
 *   /ru/guide/<section>             — section index (RU)
 *   /ru/guide/<section>/<article>   — article (RU)
 *
 * Adding an article: extend SECTIONS with a new ArticleDef.  Content file
 * is auto-discovered at `web/content/guide/<lang>/<section>/<slug>.ts`.
 */

import { LANGUAGES, DEFAULT_LANG, isLangCode, type LangCode } from '@/features/wiki/architecture';

export { LANGUAGES, DEFAULT_LANG, isLangCode };
export type { LangCode };

/* ────────────────────────────────────────────────────────────────── */
/*  Sections                                                           */
/* ────────────────────────────────────────────────────────────────── */
/*  Section = a top-level grouping.  Order = display order.            */

export interface GuideSectionDef {
  /** URL slug (kebab-case, English — same in every language). */
  slug: string;
  /** i18n key under `guide.section.<slug>.label` / `.description`. */
  i18nKey: string;
  /** Soft visual marker. Renders as a small mono badge, not a hero icon. */
  icon: string;
  /** Section kind drives the article layout chosen by the renderer. */
  kind: 'reference' | 'use-case' | 'workflow' | 'intro' | 'faq';
}

export const SECTIONS: readonly GuideSectionDef[] = [
  { slug: 'getting-started', i18nKey: 'gettingStarted', icon: '◇', kind: 'intro' },
  { slug: 'use-cases',       i18nKey: 'useCases',       icon: '◯', kind: 'use-case' },
  { slug: 'workflows',       i18nKey: 'workflows',      icon: '▷', kind: 'workflow' },
  { slug: 'reference',       i18nKey: 'reference',      icon: '□', kind: 'reference' },
  { slug: 'faq',             i18nKey: 'faq',            icon: '?', kind: 'faq' },
] as const;

export type SectionSlug = (typeof SECTIONS)[number]['slug'];

export const SECTION_SLUGS: readonly SectionSlug[] = SECTIONS.map((s) => s.slug);

export function isSectionSlug(v: unknown): v is SectionSlug {
  return typeof v === 'string' && SECTION_SLUGS.includes(v as SectionSlug);
}

export function getSectionDef(slug: SectionSlug): GuideSectionDef {
  return SECTIONS.find((s) => s.slug === slug) ?? SECTIONS[0];
}

/* ────────────────────────────────────────────────────────────────── */
/*  Articles                                                           */
/* ────────────────────────────────────────────────────────────────── */
/*  Article registry — flat list.  Each article belongs to exactly one  */
/*  section.  The registry is the source of truth for sitemap +        */
/*  prerender; content modules are auto-loaded by slug.                */

export interface GuideArticleDef {
  /** URL slug, kebab-case, English. */
  slug: string;
  /** Owning section. */
  section: SectionSlug;
  /** Optional ordering inside the section (asc).  Articles without a sort
   *  key fall to the bottom in alpha order. */
  sort?: number;
  /** Mark as a cornerstone — boosts sitemap priority + breadcrumb hint. */
  cornerstone?: boolean;
}

export const ARTICLES: readonly GuideArticleDef[] = [
  /* getting-started */
  { slug: 'what-is-the-map',     section: 'getting-started', sort: 1, cornerstone: true },
  { slug: 'first-five-minutes',  section: 'getting-started', sort: 2 },
  { slug: 'guest-vs-account',    section: 'getting-started', sort: 3 },

  /* reference (one per feature surface) */
  { slug: 'filters-panel',       section: 'reference', sort: 1 },
  { slug: 'choropleth-layers',   section: 'reference', sort: 2 },
  { slug: 'poi-layers',          section: 'reference', sort: 3 },
  { slug: 'details-panel',       section: 'reference', sort: 4 },
  { slug: 'pins',                section: 'reference', sort: 5 },
  { slug: 'bulk-import',         section: 'reference', sort: 6 },
  { slug: 'shortlist',           section: 'reference', sort: 7 },
  { slug: 'annotations',         section: 'reference', sort: 8 },
  { slug: 'compare-table',       section: 'reference', sort: 9 },
  { slug: 'visit-planner',       section: 'reference', sort: 10 },
  { slug: 'ai-helper',           section: 'reference', sort: 11 },

  /* use-cases */
  { slug: 'real-estate-listings', section: 'use-cases', sort: 1, cornerstone: true },
  { slug: 'climate-search',       section: 'use-cases', sort: 2 },
  { slug: 'family-relocation',    section: 'use-cases', sort: 3 },
  { slug: 'remote-work',          section: 'use-cases', sort: 4 },
  { slug: 'retirement',           section: 'use-cases', sort: 5 },
  { slug: 'tax-optimization',     section: 'use-cases', sort: 6 },
  { slug: 'lifestyle-fit',        section: 'use-cases', sort: 7 },
  { slug: 'visa-pathway',         section: 'use-cases', sort: 8 },
  { slug: 'scouting-trip',        section: 'use-cases', sort: 9 },
  { slug: 'final-decision',       section: 'use-cases', sort: 10 },

  /* workflows */
  { slug: 'quiz-to-decision',         section: 'workflows', sort: 1, cornerstone: true },
  { slug: 'listings-to-shortlist',    section: 'workflows', sort: 2 },
  { slug: 'scouting-to-checklist',    section: 'workflows', sort: 3 },

  /* faq */
  { slug: 'common-questions',    section: 'faq', sort: 1 },
] as const;

export type ArticleSlug = (typeof ARTICLES)[number]['slug'];

export const ARTICLE_SLUGS: readonly ArticleSlug[] = ARTICLES.map((a) => a.slug);

export function isArticleSlug(v: unknown): v is ArticleSlug {
  return typeof v === 'string' && ARTICLE_SLUGS.includes(v as ArticleSlug);
}

export function getArticleDef(slug: ArticleSlug): GuideArticleDef | null {
  return ARTICLES.find((a) => a.slug === slug) ?? null;
}

export function articlesForSection(section: SectionSlug): GuideArticleDef[] {
  const list = ARTICLES.filter((a) => a.section === section);
  return [...list].sort((a, b) => {
    const sa = a.sort ?? 9999;
    const sb = b.sort ?? 9999;
    if (sa !== sb) return sa - sb;
    return a.slug.localeCompare(b.slug);
  });
}

/* ────────────────────────────────────────────────────────────────── */
/*  URL helpers                                                        */
/* ────────────────────────────────────────────────────────────────── */

interface BuildUrlOpts {
  lang?: LangCode;
  section?: SectionSlug;
  article?: ArticleSlug;
  origin?: string;
}

export function buildGuideUrl(opts: BuildUrlOpts = {}): string {
  const lang = opts.lang ?? DEFAULT_LANG;
  const prefix = lang === DEFAULT_LANG ? '' : `/${lang}`;
  const parts: string[] = ['guide'];
  if (opts.section) parts.push(opts.section);
  if (opts.section && opts.article) parts.push(opts.article);
  const path = `${prefix}/${parts.join('/')}`;
  return opts.origin ? `${opts.origin}${path}` : path;
}

export interface ParsedGuidePath {
  lang: LangCode;
  section: SectionSlug | null;
  article: ArticleSlug | null;
}

export function parseGuidePath(pathname: string): ParsedGuidePath | null {
  const segments = pathname.split('/').filter(Boolean);
  if (segments.length === 0) return null;

  let cursor = 0;
  let lang: LangCode = DEFAULT_LANG;

  if (isLangCode(segments[cursor]) && segments[cursor] !== DEFAULT_LANG) {
    lang = segments[cursor] as LangCode;
    cursor += 1;
  }

  if (segments[cursor] !== 'guide') return null;
  cursor += 1;

  const sectionSeg = segments[cursor];
  const section = isSectionSlug(sectionSeg) ? sectionSeg : null;
  cursor += section ? 1 : 0;

  const articleSeg = segments[cursor];
  const article = isArticleSlug(articleSeg) ? articleSeg : null;

  return { lang, section, article };
}

/* ────────────────────────────────────────────────────────────────── */
/*  Hreflang                                                           */
/* ────────────────────────────────────────────────────────────────── */

export interface GuideHreflangEntry {
  hreflang: string;
  href: string;
}

export function buildGuideHreflangAlternates(
  base: Omit<BuildUrlOpts, 'lang'> & { origin: string },
): GuideHreflangEntry[] {
  const entries: GuideHreflangEntry[] = LANGUAGES.map((l) => ({
    hreflang: l.code,
    href: buildGuideUrl({ ...base, lang: l.code as LangCode }),
  }));
  entries.push({
    hreflang: 'x-default',
    href: buildGuideUrl({ ...base, lang: DEFAULT_LANG }),
  });
  return entries;
}
